import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { BankAccount, ConsentData } from '@ekhodealer/ekho-common';
import type {
	CreateBankAccountsFromPlaidDataInput,
	LinkTokenResponse,
} from './types';

import { apiClient } from '@config/server/apiClient';
import { configurationEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API functions for Plaid/banking operations.
 * All endpoints route through the admin portal namespace:
 *   /admin_portal/:spId/configuration/banking/*
 */

export const plaidApi = {
	createLinkToken: async (servicesProviderId: string): Promise<LinkTokenResponse> => {
		const response = await apiClient.post<LinkTokenResponse>(
			configurationEndpoints.bankingCreateLinkToken(servicesProviderId),
		);
		return response.data;
	},

	createBankAccountsFromPlaidData: async (
		input: CreateBankAccountsFromPlaidDataInput,
		consentData: ConsentData,
	): Promise<BankAccount[]> => {
		const response = await apiClient.post<BankAccount[]>(
			configurationEndpoints.bankingCreateBankAccountsFromPlaidData(input.servicesProviderId),
			{
				...input,
				consentData,
			},
		);
		return response.data;
	},

	getBankAccounts: async (servicesProviderId: string): Promise<BankAccount[]> => {
		const response = await apiClient.get<BankAccount[]>(
			configurationEndpoints.bankingGetBankAccounts(servicesProviderId),
		);
		return response.data;
	},

	setPrimaryBankAccount: async (
		servicesProviderId: string,
		bankAccountId: string,
	): Promise<BankAccount[]> => {
		const response = await apiClient.patch<BankAccount[]>(
			configurationEndpoints.bankingSetPrimaryBankAccount(servicesProviderId, bankAccountId),
		);
		return response.data;
	},
};

/**
 * Hook for creating a link token for Plaid
 */
export const useCreateLinkToken = (servicesProviderId: string) => {
	return useQuery({
		queryKey: queries.plaid.linkToken(servicesProviderId).queryKey,
		queryFn: () => plaidApi.createLinkToken(servicesProviderId),
		enabled: !!servicesProviderId,
		staleTime: 5 * 60 * 1000, // 5 minutes - link tokens expire
		retry: 1, // Only retry once for link tokens
	});
};

/**
 * Hook for fetching bank accounts for a services provider
 */
export const useGetBankAccounts = (servicesProviderId: string) => {
	return useQuery({
		queryKey: queries.plaid.bankAccounts(servicesProviderId).queryKey,
		queryFn: () => plaidApi.getBankAccounts(servicesProviderId),
		enabled: !!servicesProviderId,
		staleTime: 30 * 1000, // 30 seconds - bank account data can change
	});
};

/**
 * Hook for creating a bank account from Plaid data
 */
export const useCreateBankAccountsFromPlaidData = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			input,
			consentData,
		}: {
			input: CreateBankAccountsFromPlaidDataInput;
			consentData: ConsentData;
		}) => plaidApi.createBankAccountsFromPlaidData(input, consentData),
		onSuccess: (data: BankAccount[], variables) => {
			// Invalidate and refetch bank accounts for this services provider
			queryClient.invalidateQueries({
				queryKey: queries.plaid.bankAccounts(variables.input.servicesProviderId).queryKey,
			});

			// Update the cache with the new bank accounts data
			queryClient.setQueryData(
				queries.plaid.bankAccounts(variables.input.servicesProviderId).queryKey,
				data,
			);
		},
	});
};

/**
 * Hook for setting a primary bank account
 */
export const useSetPrimaryBankAccount = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			servicesProviderId,
			bankAccountId,
		}: {
			servicesProviderId: string;
			bankAccountId: string;
		}) => plaidApi.setPrimaryBankAccount(servicesProviderId, bankAccountId),
		onSuccess: (data: BankAccount[], variables) => {
			// Update the cache with the new bank accounts data
			queryClient.setQueryData(
				queries.plaid.bankAccounts(variables.servicesProviderId).queryKey,
				data,
			);
		},
	});
};

// Keep the legacy plaidService for backward compatibility during migration
export class PlaidService {
	async createLinkToken(servicesProviderId: string): Promise<LinkTokenResponse> {
		return plaidApi.createLinkToken(servicesProviderId);
	}

	async createBankAccountsFromPlaidData(
		input: CreateBankAccountsFromPlaidDataInput,
		consentData: ConsentData,
	): Promise<BankAccount[]> {
		return plaidApi.createBankAccountsFromPlaidData(input, consentData);
	}

	async getBankAccounts(servicesProviderId: string): Promise<BankAccount[]> {
		return plaidApi.getBankAccounts(servicesProviderId);
	}

	async setPrimaryBankAccount(
		servicesProviderId: string,
		bankAccountId: string,
	): Promise<BankAccount[]> {
		return plaidApi.setPrimaryBankAccount(servicesProviderId, bankAccountId);
	}
}

// Export singleton instance for backward compatibility
export const plaidService = new PlaidService();
